import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import api from '../../services/api';
import { CALL_SHORTCUTS } from './contact';

interface Props {
  open: boolean;
  callId: number | null;
  customerName?: string;
  onClose: () => void;
  onDone?: () => void;
}

/** Marca un llamado como hecho y, con un toque, agenda el próximo. */
const CompleteCallDialog: React.FC<Props> = ({ open, callId, customerName, onClose, onDone }) => {
  const [notes, setNotes] = useState('');
  const [nextInDays, setNextInDays] = useState<number | null>(30);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setNotes('');
    setNextInDays(30);
    setError(null);
  }, [open]);

  const save = async () => {
    if (!callId) return;
    setSaving(true);
    setError(null);
    try {
      await api.completeCall(callId, { notes: notes || undefined, nextInDays: nextInDays ?? undefined });
      onDone?.();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Llamado hecho{customerName ? ` · ${customerName}` : ''}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="¿Qué quedó? (opcional)"
            placeholder="Ej: pide 20 unidades para el viernes"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            multiline
            minRows={2}
            autoFocus
          />
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Próximo llamado</Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              <Chip
                label="No agendar"
                color={nextInDays == null ? 'primary' : 'default'}
                variant={nextInDays == null ? 'filled' : 'outlined'}
                onClick={() => setNextInDays(null)}
              />
              {CALL_SHORTCUTS.map(s => (
                <Chip
                  key={s.days}
                  label={s.label}
                  color={nextInDays === s.days ? 'primary' : 'default'}
                  variant={nextInDays === s.days ? 'filled' : 'outlined'}
                  onClick={() => setNextInDays(s.days)}
                />
              ))}
            </Stack>
          </Box>
          {error && <Typography color="error" variant="body2">{error}</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" color="success" onClick={save} disabled={saving}>
          {saving ? 'Guardando…' : 'Listo'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompleteCallDialog;
